import type { ChallengeDefinition } from "../domain/schema";
import { shuffleAnswerPresentation } from "./presentationOrder";

interface Options {
  panel: HTMLElement;
  onComplete(challenge: ChallengeDefinition, hintsUsed: number): void;
  onClose(): void;
  speak(text: string): void;
}

export class MuscleChallengeController {
  private challenge?: ChallengeDefinition;
  private hintsUsed = 0;
  private seenStates = new Set<string>();

  constructor(private readonly options: Options) {}

  open(challenge: ChallengeDefinition): void {
    this.challenge = challenge;
    this.hintsUsed = 0;
    this.seenStates.clear();
    this.options.panel.hidden = false;
    this.renderShell();
    shuffleAnswerPresentation(this.options.panel);
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.focus();
  }

  close(): void {
    this.options.panel.hidden = true;
    this.options.panel.innerHTML = "";
    this.options.onClose();
  }

  private renderShell(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.options.panel.innerHTML = `
      <div class="challenge-heading">
        <div><span class="pill">Muscle Meadow field station</span><h2 id="challenge-title">${challenge.title}</h2></div>
        <button class="icon-button" data-action="close" type="button" aria-label="Return to the meadow">×</button>
      </div>
      <div id="challenge-body" class="challenge-body"></div>
      <div class="hint-row">
        <button class="secondary-button secondary-button--compact" data-action="hint" type="button">Need a hint?</button>
        <p id="hint-text" class="hint-text" aria-live="polite">Observe the model first. You can always ask for help.</p>
      </div>
      <output id="challenge-feedback" class="challenge-feedback" aria-live="polite"></output>
    `;
    this.options.panel
      .querySelector("[data-action='close']")
      ?.addEventListener("click", () => this.close());
    this.options.panel
      .querySelector("[data-action='hint']")
      ?.addEventListener("click", () => this.showHint());
    if (challenge.id === "muscle.pair-power") this.renderPairPower();
    else if (challenge.id === "muscle.book-lift") this.renderBookLift();
    else if (challenge.id === "muscle.elastic-model") this.renderElasticModel();
    else this.renderHealthyTrail();
  }

  private body(): HTMLElement {
    const body =
      this.options.panel.querySelector<HTMLElement>("#challenge-body");
    if (!body) throw new Error("Challenge body missing");
    return body;
  }

  private feedback(message: string, success = false): void {
    const output = this.options.panel.querySelector<HTMLOutputElement>(
      "#challenge-feedback",
    );
    if (!output) return;
    output.textContent = message;
    output.classList.toggle("challenge-feedback--success", success);
    if (success || this.hintsUsed > 0) this.options.speak(message);
  }

  private showHint(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.hintsUsed = Math.min(2, this.hintsUsed + 1);
    const hint = challenge.hints[this.hintsUsed - 1] ?? challenge.hints[1];
    const text = this.options.panel.querySelector<HTMLElement>("#hint-text");
    if (text) text.textContent = hint;
    this.options.speak(hint);
    const button = this.options.panel.querySelector<HTMLButtonElement>(
      "[data-action='hint']",
    );
    if (button) {
      button.textContent =
        this.hintsUsed === 1 ? "Show a bigger hint" : "Hint shown";
      button.disabled = this.hintsUsed === 2;
    }
  }

  private finish(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.feedback(challenge.successExplanation, true);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "primary-button finish-button";
    button.textContent = "Restore this Motion Spark";
    button.addEventListener("click", () => {
      this.options.onComplete(challenge, this.hintsUsed);
      this.close();
    });
    this.body().append(button);
  }

  private renderPairPower(): void {
    this.body().innerHTML = `
      <p>Operate the human arm model. Watch both upper-arm muscles as the lower-arm bone moves.</p>
      <div class="arm-lab">
        <div id="pair-arm" class="arm-diagram arm-diagram--straight" aria-label="Human arm model is straight">${armDiagram()}</div>
        <div class="muscle-readouts">
          <div><span class="muscle-swatch muscle-swatch--front"></span><strong>Front muscle</strong><output id="front-state">relaxed: longer and thinner</output></div>
          <div><span class="muscle-swatch muscle-swatch--back"></span><strong>Back muscle</strong><output id="back-state">contracted: shorter and thicker</output></div>
        </div>
      </div>
      <div class="mechanism-controls">
        <button type="button" data-arm-state="bent">Bend the arm</button>
        <button type="button" data-arm-state="straight">Straighten the arm</button>
      </div>
      <div id="pair-question"></div>
    `;
    for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
      "[data-arm-state]",
    )) {
      button.addEventListener("click", () => {
        const state = button.dataset.armState ?? "straight";
        this.seenStates.add(state);
        const diagram =
          this.options.panel.querySelector<HTMLElement>("#pair-arm");
        diagram?.classList.toggle("arm-diagram--bent", state === "bent");
        diagram?.classList.toggle(
          "arm-diagram--straight",
          state === "straight",
        );
        diagram?.setAttribute("aria-label", `Human arm model is ${state}`);
        const front =
          this.options.panel.querySelector<HTMLOutputElement>("#front-state");
        const back =
          this.options.panel.querySelector<HTMLOutputElement>("#back-state");
        if (front)
          front.value =
            state === "bent"
              ? "contracted: shorter and thicker"
              : "relaxed: longer and thinner";
        if (back)
          back.value =
            state === "bent"
              ? "relaxed: longer and thinner"
              : "contracted: shorter and thicker";
        this.feedback(
          state === "bent"
            ? "The front muscle contracts and pulls the lower-arm bone up while its partner relaxes."
            : "Their jobs swap: the back muscle contracts while the front muscle relaxes.",
          true,
        );
        if (this.seenStates.size === 2) this.renderPairQuestion();
      });
    }
  }

  private renderPairQuestion(): void {
    const area =
      this.options.panel.querySelector<HTMLElement>("#pair-question");
    if (!area || area.childElementCount) return;
    area.innerHTML = `
      <p class="challenge-question">When the arm bends, what does the contracting front muscle do?</p>
      <div class="choice-grid">
        <button type="button" data-pair-answer="wrong">It pushes the bone and becomes longer</button>
        <button type="button" data-pair-answer="correct">It pulls the bone and becomes shorter and thicker</button>
        <button type="button" data-pair-answer="wrong">It turns into bone</button>
      </div>
    `;
    shuffleAnswerPresentation(area);
    for (const button of area.querySelectorAll<HTMLButtonElement>(
      "[data-pair-answer]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.pairAnswer !== "correct") {
          this.feedback(
            "Muscles pull rather than push. Operate the bent arm again and watch the front muscle.",
          );
          return;
        }
        this.finish();
      });
    }
  }

  private renderBookLift(): void {
    this.body().innerHTML = `
      <p>Move the slider to lift the model book. Observe the front and back muscles rather than judging strength or speed.</p>
      <div id="lift-arm" class="arm-diagram arm-diagram--straight arm-diagram--book" aria-label="Arm holding a book at rest">${armDiagram()}<span class="model-book" aria-hidden="true">BOOK</span></div>
      <label class="lift-control">Book position
        <input id="book-slider" type="range" min="0" max="2" value="0" step="1" />
        <output id="book-position">resting</output>
      </label>
      <div id="lift-question"></div>
    `;
    const slider =
      this.options.panel.querySelector<HTMLInputElement>("#book-slider");
    slider?.addEventListener("input", () => {
      const value = Number(slider.value);
      const diagram =
        this.options.panel.querySelector<HTMLElement>("#lift-arm");
      diagram?.classList.toggle("arm-diagram--bent", value === 2);
      diagram?.classList.toggle("arm-diagram--straight", value !== 2);
      const output =
        this.options.panel.querySelector<HTMLOutputElement>("#book-position");
      if (output)
        output.value =
          value === 0 ? "resting" : value === 1 ? "halfway" : "lifted";
      if (value === 2) {
        this.feedback(
          "The book rises as the front muscle shortens, thickens, and pulls the lower-arm bone.",
          true,
        );
        this.renderLiftQuestion();
      }
    });
  }

  private renderLiftQuestion(): void {
    const area =
      this.options.panel.querySelector<HTMLElement>("#lift-question");
    if (!area || area.childElementCount) return;
    area.innerHTML = `
      <p class="challenge-question">Which muscle state did you observe while the book lifted?</p>
      <div class="choice-grid">
        <button type="button" data-lift-answer="correct">Front contracts and thickens; back relaxes and thins</button>
        <button type="button" data-lift-answer="wrong">Both muscles disappear</button>
        <button type="button" data-lift-answer="wrong">Front relaxes while no muscle pulls</button>
      </div>
    `;
    shuffleAnswerPresentation(area);
    for (const button of area.querySelectorAll<HTMLButtonElement>(
      "[data-lift-answer]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.liftAnswer !== "correct") {
          this.feedback(
            "Look at the lifted model again. One muscle contracts while its partner relaxes.",
          );
          return;
        }
        this.finish();
      });
    }
  }

  private renderElasticModel(): void {
    this.body().innerHTML = `
      <p>This follows the chapter's card-arm activity. Card strips represent bones, a fastener represents the elbow joint, and elastic bands represent paired muscles.</p>
      <div id="elastic-arm" class="elastic-model" aria-label="Card and elastic arm model">${elasticModelDiagram()}</div>
      <div class="mechanism-controls">
        <button type="button" data-elastic="front">Pull front elastic</button>
        <button type="button" data-elastic="back">Pull back elastic</button>
      </div>
      <div id="model-question"></div>
    `;
    for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
      "[data-elastic]",
    )) {
      button.addEventListener("click", () => {
        const side = button.dataset.elastic ?? "front";
        this.seenStates.add(side);
        const model =
          this.options.panel.querySelector<HTMLElement>("#elastic-arm");
        model?.setAttribute("data-pulled", side);
        this.feedback(
          side === "front"
            ? "Pulling the front elastic bends the card arm."
            : "Pulling the back elastic straightens the card arm.",
          true,
        );
        if (this.seenStates.size === 2) this.renderModelQuestion();
      });
    }
  }

  private renderModelQuestion(): void {
    const area =
      this.options.panel.querySelector<HTMLElement>("#model-question");
    if (!area || area.childElementCount) return;
    area.innerHTML = `
      <p class="challenge-question">How is this model useful, and how is it different from a real arm?</p>
      <div class="choice-grid">
        <button type="button" data-model-answer="wrong">Elastic bands are exactly the same as living muscles</button>
        <button type="button" data-model-answer="correct">It shows paired pulling around a joint but leaves out living tissues</button>
        <button type="button" data-model-answer="wrong">Real muscles push card bones</button>
      </div>
    `;
    shuffleAnswerPresentation(area);
    for (const button of area.querySelectorAll<HTMLButtonElement>(
      "[data-model-answer]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.modelAnswer !== "correct") {
          this.feedback(
            "A scientific model is useful but simplified. Choose both a similarity and a limitation.",
          );
          return;
        }
        this.finish();
      });
    }
  }

  private renderHealthyTrail(): void {
    const activities = [
      "Walking",
      "Dancing",
      "Cycling",
      "Climbing",
      "Playing ball",
    ];
    const benefits = [
      ["heart", "Helps the heart and lungs work well"],
      ["strong", "Helps muscles and bones grow stronger"],
      ["sleep", "Can support sleep"],
      ["think", "Can help us think"],
      ["mood", "Can lift our mood"],
    ];
    this.body().innerHTML = `
      <p>Build a varied movement trail. There is no fastest or best body—choose three enjoyable ways to move and two benefits from the chapter.</p>
      <form id="health-form">
        <fieldset><legend>Choose at least three ways to move</legend><div class="activity-choices">${activities.map((activity) => `<label><input type="checkbox" name="activity" value="${activity}" /><span>${activity}</span></label>`).join("")}</div></fieldset>
        <fieldset><legend>Choose at least two ways movement can help</legend><div class="benefit-choices">${benefits.map(([id, label]) => `<label><input type="checkbox" name="benefit" value="${id}" /><span>${label}</span></label>`).join("")}</div></fieldset>
        <label class="unit-choice">Remember Skeleton Safari: what do muscles pull to move the body?
          <select id="retrieval-answer"><option value="">Choose</option><option value="bones">Bones</option><option value="fur">Fur</option><option value="air">Air</option></select>
        </label>
        <button class="primary-button" type="submit">Submit answers</button>
      </form>
    `;
    this.options.panel
      .querySelector<HTMLFormElement>("#health-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const activitiesChosen = this.options.panel.querySelectorAll(
          "input[name='activity']:checked",
        ).length;
        const benefitsChosen = this.options.panel.querySelectorAll(
          "input[name='benefit']:checked",
        ).length;
        const retrieval =
          this.options.panel.querySelector<HTMLSelectElement>(
            "#retrieval-answer",
          )?.value;
        if (activitiesChosen < 3) {
          this.feedback(
            "Choose at least three different enjoyable movements. No choice is a score or judgment.",
          );
          return;
        }
        if (benefitsChosen < 2) {
          this.feedback(
            "Choose at least two ways movement can support health.",
          );
          return;
        }
        if (retrieval !== "bones") {
          this.feedback(
            "Remember the skeleton: muscles pull on bones to move the body.",
          );
          return;
        }
        this.finish();
      });
  }
}

function armDiagram(): string {
  return `<svg viewBox="0 0 360 240" aria-hidden="true">
    <g class="arm-bones" fill="none" stroke-linecap="round"><path class="upper-bone" d="M122 38 L124 142"/><path class="lower-bone" d="M125 144 L250 178"/><circle cx="124" cy="143" r="13"/></g>
    <path class="front-muscle" d="M91 55 Q126 32 151 60 L145 124 Q124 143 101 122Z"/>
    <path class="back-muscle" d="M143 55 Q171 69 158 128 L144 137 Q151 86 143 55Z"/>
    <path class="pull-arrow" d="M175 123 Q203 124 215 146 M204 137 L216 147 L202 151"/>
    <text x="42" y="31">upper arm</text><text x="230" y="208">lower arm</text>
  </svg>`;
}

function elasticModelDiagram(): string {
  return `<svg viewBox="0 0 420 230" aria-hidden="true">
    <rect class="card-upper" x="115" y="25" width="48" height="145" rx="8"/>
    <rect class="card-lower" x="145" y="145" width="170" height="45" rx="8"/>
    <circle class="fastener" cx="151" cy="161" r="13"/>
    <path class="elastic-front" d="M121 49 Q184 97 254 157"/><path class="elastic-back" d="M157 48 Q132 105 158 158"/>
    <text x="40" y="50">card “bone”</text><text x="280" y="215">fastener “joint”</text>
  </svg>`;
}
